// How to play Trouble Brewing: the first game, for players who have never played.
import type { ScriptLoad } from "../../hooks/useScript";
import {
  CharacterSection,
  DaySteps,
  EveryoneTips,
  NightBasics,
  ScriptCharacters,
  Section,
  SetupSection,
  WhoIsExecuted,
  type GuideSection,
} from "./shared";

export const TROUBLE_BREWING_SECTIONS: GuideSection[] = [
  { id: "setup", label: "Setup" },
  { id: "character", label: "Your character" },
  { id: "night", label: "Night" },
  { id: "day", label: "Day" },
  { id: "dying", label: "Dying" },
  { id: "clues", label: "False clues" },
  { id: "winning", label: "Winning" },
  { id: "tips", label: "Tips" },
  { id: "characters", label: "Characters" },
];

export function TroubleBrewingIntro() {
  return (
    <section className="card stack">
      <h3>The short version</h3>
      <ul>
        <li>
          Everyone secretly gets a character. Most players are{" "}
          <strong className="good">good</strong>. A few are <strong className="evil">evil</strong>,
          and one of them is the <strong className="evil">Demon</strong>.
        </li>
        <li>
          Each <strong>night</strong>, the Demon kills a player and some good players learn a clue.
        </li>
        <li>
          Each <strong>day</strong>, everyone talks it over and can vote to execute one player.
        </li>
        <li>
          <strong className="good">Good wins</strong> when the Demon dies.{" "}
          <strong className="evil">Evil wins</strong> when only two players are left alive.
        </li>
      </ul>
      <p className="small muted">
        The evil players know who each other are. The good players don&apos;t, so they have to work
        it out by talking, and the evil players will lie to them.
      </p>
    </section>
  );
}

export function TroubleBrewingGuide({ script }: { script: ScriptLoad }) {
  return (
    <>
      <SetupSection gameName="Trouble Brewing" />
      <CharacterSection />

      <Section id="night" title="Night">
        <NightBasics />
        <div className="card card-tight stack-sm">
          <strong>The first night is different</strong>
          <p className="small">
            Nobody dies. With 7 or more players, the evil players learn who their teammates are, and
            the Demon is shown three good characters that are not in the game. Those are safe to
            pretend to be. In a smaller game, evil players have to find each other.
          </p>
        </div>
      </Section>

      <Section id="day" title="Day">
        <DaySteps />
        <WhoIsExecuted />
        <p className="small muted">
          The <strong>Slayer</strong> can shoot one player per game during the day: tap the player,
          then <strong>Slayer shot</strong>. If that player is the Demon, they die. Anyone can
          pretend to be the Slayer and shoot, but it only works for the real one.
        </p>
      </Section>

      <Section id="dying" title="When you die">
        <p>
          You are not out. Dead players keep talking, keep helping their team, and win or lose with
          it.
        </p>
        <ul>
          <li>
            Your ability stops working (unless your card says otherwise), you can&apos;t nominate,
            and you sleep through the night.
          </li>
          <li>
            You get one <strong>ghost vote</strong> for the rest of the game. Voting Yes uses it up.
            Voting No does not.
          </li>
        </ul>
      </Section>

      <Section id="clues" title="Not every clue is true">
        <p>Some good players are given wrong information, and they are never told.</p>
        <ul>
          <li>
            The <strong>Drunk</strong> is shown a Townsfolk card and believes it. They have no real
            ability, and their clues can&apos;t be trusted.
          </li>
          <li>
            The <strong>Poisoner</strong> poisons one player each night. That player&apos;s ability
            fails for that night and the next day, and their clues can&apos;t be trusted.
          </li>
          <li>
            A couple of characters can show up as the wrong team to other players&apos; abilities.
          </li>
        </ul>
        <p>
          So compare notes, look for clues that don&apos;t fit together, and don&apos;t trust
          anything completely, even your own.
        </p>
      </Section>

      <Section id="winning" title="How to win">
        <div className="card winner-good stack-sm">
          <strong className="good">Good wins</strong>
          <p>when the Demon dies, usually by being executed.</p>
        </div>
        <div className="card winner-evil stack-sm">
          <strong className="evil">Evil wins</strong>
          <p>when only two players are left alive.</p>
        </div>
        <p className="small muted">
          A few characters change this. If the Saint is executed, good loses. If only three players
          are alive, the Mayor is one of them, and nobody is executed that day, good wins. If the
          Demon dies while five or more players are alive, the Scarlet Woman becomes the new Demon
          and the game goes on.
        </p>
        <p className="small muted">
          When the game ends, everyone sees who was who, including who was drunk or poisoned.
        </p>
      </Section>

      <Section id="tips" title="Tips for your first game">
        <h3 className="good">If you are good</h3>
        <ul>
          <li>Share what you learn. Good wins by putting everyone&apos;s clues together.</li>
          <li>
            You don&apos;t have to say your character straight away. If you look useful, the Demon
            may kill you tonight.
          </li>
          <li>
            Use your vote. Someone dies every night, so a day without an execution usually helps
            evil.
          </li>
        </ul>
        <h3 className="evil">If you are evil</h3>
        <ul>
          <li>
            Pretend to be a good character. The three characters the Demon was shown are the safest,
            so the Demon should quietly pass them on to the Minions.
          </li>
          <li>
            Make up clues that fit your story. Back up your teammates, but not so much that it looks
            suspicious.
          </li>
        </ul>
        <EveryoneTips />
      </Section>

      <Section id="characters" title="The characters">
        <ScriptCharacters script={script} />
      </Section>
    </>
  );
}
